/* Karte öffnen: Server-Karten (Liste mit „Öffnen“ je Karte; Versionen, Teilen, Umbenennen und Löschen hinter „⋯“) und lokal (Datei, Beispiel). Gespeichert wird im SaveDialog. */
import { useCallback, useEffect, useState } from 'react';
import type { BackendSession } from '../net/session.ts';
import type { CloudLink } from '../net/cloud.ts';
import type { MapMeta, RevisionList } from '../net/backend.ts';
import I18n from '../i18n.ts';
import { Dialog, DialogBody, DialogFoot } from './dialog.tsx';
import { openDialog } from './modal.tsx';
import { ShareDialog } from './ShareDialog.tsx';
import { useStore, useT } from './hooks.ts';

export interface CloudHost {
  /** Neuer Name der verbundenen Karte (Umbenennen im Dialog) */
  setFileName(name: string): void;
  /** Dateiauswahl des Browsers öffnen (Karte von der Festplatte) */
  openLocal(): void;
  /** Vordefinierte Karten */
  samples(): { id: string; label: string }[];
  openSample(id: string): void;
  /** Anmeldedialog; true, wenn angemeldet */
  login(): Promise<boolean>;
}

const localTime = (s: string): string => { const d = new Date(String(s).replace(' ', 'T')); return isNaN(d.getTime()) ? '' : d.toLocaleString(I18n.lang); };
const kb = (n: number): number => Math.max(1, Math.round(n / 1024));

export function CloudDialog(props: { session: BackendSession; cloud: CloudLink; host: CloudHost; onClose: () => void }) {
  const t = useT();
  const { session, cloud, host } = props;
  const link = useStore(cloud).link;
  const sess = useStore(session);
  const server = sess.state === 'ok', authed = server && sess.authed;
  const [items, setItems] = useState<MapMeta[] | null>(null);
  const [revs, setRevs] = useState<{ meta: MapMeta; list: RevisionList } | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [menu, setMenu] = useState<string | null>(null);   // Karte, deren „⋯“-Aktionen offen sind

  const refresh = useCallback(async () => {
    try { setItems(await (session.client as NonNullable<typeof session.client>).mapList()); }
    catch (e) { if ((e as { status?: number }).status !== 401) setMsg(t('cloud.list_failed', { msg: (e as Error).message })); }
  }, [session, t]);
  useEffect(() => { if (authed) void refresh(); else setItems(null); }, [refresh, authed]);

  const guard = async (fn: () => Promise<void>) => {
    try { await fn(); } catch (e) { const err = e as { status?: number; data?: unknown; message: string }; setMsg(err.status === 409 && !err.data ? t('cloud.name_taken') : t('cloud.save_failed', { msg: err.message })); }
  };
  const open = (m: MapMeta) => guard(async () => { await cloud.open(m.id); props.onClose(); });
  const rename = (m: MapMeta) => guard(async () => {
    const n = window.prompt(t('cloud.new_name'), m.name);
    if (!n || n === m.name) return;
    const r = await (session.client as NonNullable<typeof session.client>).mapSave(m.id, m.rev, { name: n });
    cloud.renamed(m.id, r);
    if (link && link.id === m.id) host.setFileName(r.name);
    setMsg(t('cloud.renamed', { name: r.name })); await refresh();
  });
  const remove = (m: MapMeta) => guard(async () => {
    if (!window.confirm(t('cloud.confirm_delete', { name: m.name }))) return;
    await (session.client as NonNullable<typeof session.client>).mapRemove(m.id);
    cloud.removed(m.id);
    if (revs && revs.meta.id === m.id) setRevs(null);
    setMsg(t('cloud.deleted', { name: m.name })); await refresh();
  });
  const showVersions = (m: MapMeta) => guard(async () => {
    if (revs && revs.meta.id === m.id) { setRevs(null); return; }
    setRevs({ meta: m, list: await (session.client as NonNullable<typeof session.client>).mapRevisions(m.id) });
  });
  const loadVersion = (m: MapMeta, rev: number) => guard(async () => { await cloud.openRevision(m, rev); props.onClose(); });
  const share = (m: MapMeta) => { void openDialog((close) => <ShareDialog client={session.client as NonNullable<typeof session.client>} map={m} onClose={() => close()} />); };
  // Von jemand anderem geteilte Karte: nur öffnen und die Freigabe verlassen; umbenennen, löschen und teilen darf der Besitzer
  const leave = (m: MapMeta) => guard(async () => {
    if (!window.confirm(t('share.confirm_leave', { name: m.name }))) return;
    await (session.client as NonNullable<typeof session.client>).shareRemove(m.id, (session.getSnapshot().user as { id: string }).id);
    cloud.removed(m.id);
    setMsg(t('share.left', { name: m.name })); await refresh();
  });

  const intro = items && !items.length ? t('cloud.none') : t('cloud.intro');
  const act = (fn: () => void) => () => { setMenu(null); fn(); };
  return (
    <Dialog title={t('cloud.title')} onClose={props.onClose}>
      <DialogBody>
        {server && <h3>{t('cloud.section_server')}</h3>}
        {server && !authed && (
          <div className="row">
            <span className="small">{t('cloud.signed_out')}</span>
            <button className="btn" onClick={() => void host.login()}>{t('cloud.sign_in')}</button>
          </div>
        )}
        {authed && <p className="small" role="status">{msg ?? intro}</p>}
        <div className="belist">
          {items?.map((m) => {
            const cur = !!link && link.id === m.id;
            const mine = !m.access || m.access === 'owner' || m.access === 'admin';
            return (
              <div key={m.id} className={'row berow' + (cur ? ' cur' : '')}>
                <span className="bepath">{m.name + (cur ? ' ●' : '')}</span>
                {!mine && <span className="pill info" title={m.ownerEmail}>{t(m.access === 'write' ? 'share.badge_write' : 'share.badge_read', { owner: m.ownerEmail || '' })}</span>}
                <span className="small">{t('cloud.meta', { kb: kb(m.size), rev: m.rev, time: localTime(m.updated) })}</span>
                <button className="btn primary" onClick={() => void open(m)}>{t('cloud.open')}</button>
                <button className="btn" aria-label={t('cloud.more')} aria-expanded={menu === m.id} onClick={() => setMenu(menu === m.id ? null : m.id)}>⋯</button>
                {menu === m.id && (
                  <div className="row" style={{ flexBasis: '100%', justifyContent: 'flex-end' }}>
                    {cur && <button className="btn sm" onClick={act(() => void showVersions(m))}>{t('cloud.versions')}</button>}
                    {mine && <button className="btn sm" onClick={act(() => share(m))}>{t('share.button')}</button>}
                    {mine && <button className="btn sm" onClick={act(() => void rename(m))}>{t('cloud.rename')}</button>}
                    {mine && <button className="btn sm" onClick={act(() => void remove(m))}>{t('cloud.delete')}</button>}
                    {!mine && <button className="btn sm" onClick={act(() => void leave(m))}>{t('share.leave')}</button>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {revs && (
          <fieldset>
            <legend>{t('cloud.versions_of', { name: revs.meta.name })}</legend>
            {revs.list.items.length ? revs.list.items.map((r) => (
              <div key={r.rev} className="row berow">
                <span className="bepath">{t('cloud.version', { rev: r.rev })}</span>
                <span className="small">{t('cloud.meta_v', { kb: kb(r.size), time: localTime(r.created) })}</span>
                <button className="btn" onClick={() => void loadVersion(revs.meta, r.rev)}>{t('cloud.load_version')}</button>
              </div>
            )) : <p className="small">{t('cloud.no_versions')}</p>}
          </fieldset>
        )}
        <h3>{t('cloud.section_local')}</h3>
        <div className="row">
          <button className="btn" onClick={() => { props.onClose(); host.openLocal(); }}>{t('cloud.from_file')}</button>
          <select className="sel" aria-label={t('app.sample.aria')} value="" onChange={(e) => { const id = e.target.value; if (id) { props.onClose(); host.openSample(id); } }}>
            <option value="">{t('app.sample.placeholder')}</option>
            {host.samples().map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
          </select>
        </div>
      </DialogBody>
      <DialogFoot><button className="btn" onClick={props.onClose}>{t('ui.close')}</button></DialogFoot>
    </Dialog>
  );
}
